// A plain grid over any one of the fourteen tables.
//
// This is where reference data is maintained, which is the whole of what the
// `Admin` role adds. That it is a control rather than a convenience is worth
// stating: with reference data editable by everyone, the source accumulated
// eleven `OwnerDepartments` values against seven permitted ones -- the drift
// phase 3 of the migration existed to clean up. Restricting it is what stops
// the drift returning.

import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DataGrid, type GridColumn } from '../grid/DataGrid';
import { Screen } from '../shell/Screen';
import { COLLECTIONS, REFERENCES } from '../schema';
import { useCollection, useLookups, useMe } from '../data';
import { actionOwner, enteredBy } from '../format';
import type { Row } from '../types';

export function CollectionScreen() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const collection = COLLECTIONS[slug];
  const rows = useCollection(slug);
  const me = useMe();
  const references = useMemo(
    () => (collection ? collection.fields.map((f) => f.name).filter((n) => REFERENCES[n]) : []),
    [collection]
  );
  const lookups = useLookups(references);

  const columns = useMemo<GridColumn<Row>[]>(() => {
    if (!collection) return [];
    return collection.fields
      // The audit columns are on the record rather than the grid: four columns
      // of provenance on every table would push the data off the screen. So are
      // the directory's keys for who raised a barrier, who acts on it and who
      // last changed Hot, GUIDs nobody reads; the name beside each says who.
      .filter(
        (field) =>
          ![
            'created_at',
            'created_by',
            'updated_at',
            'updated_by',
            'entered_by_object_id',
            'action_owner_object_id',
            'hot_changed_by_object_id',
          ].includes(field.name)
      )
      .map((field) => {
        const reference = REFERENCES[field.name];
        return {
          key: field.name,
          label: field.label,
          type: field.type,
          width: field.type === 'str' && (field.maxLength ?? 0) > 200 ? 320 : undefined,
          align: field.type === 'int' && field.name !== collection.pk ? ('right' as const) : undefined,
          value: reference
            ? (row: Row) =>
                typeof row[field.name] === 'number'
                  ? lookups.data?.[field.name]?.get(row[field.name] as number) ?? row[field.name]
                  : row[field.name]
            : field.name === 'entered_by_name'
              ? (row: Row) => enteredBy(row)
              : field.name === 'action_owner'
                ? (row: Row) => actionOwner(row)
                : undefined,
        };
      });
  }, [collection, lookups.data]);

  if (!collection) {
    return <Screen title="Not found">{`There is no collection named ${slug}.`}</Screen>;
  }

  const mayWrite = collection.writeRole === 'User' || Boolean(me.data?.is_admin);

  return (
    <Screen
      title={collection.label}
      description={
        collection.writeRole === 'Admin'
          ? 'Reference data. Only an Admin may change it.'
          : undefined
      }
      loading={rows.loading}
      error={rows.error}
      count={rows.data?.length ?? 0}
      actions={
        mayWrite ? (
          <button className="primary" onClick={() => navigate(`/data/${slug}/new`)}>
            New
          </button>
        ) : null
      }
    >
      <DataGrid
        rows={rows.data ?? []}
        columns={columns}
        getRowId={(row) => String(row[collection.pk])}
        onRowClick={(row) => navigate(`/data/${slug}/${row[collection.pk]}`)}
        emptyMessage={`No ${collection.label.toLowerCase()}.`}
      />
    </Screen>
  );
}
